import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { CaseBulkUpdateDto, CaseResponseDto } from '@bandido/shared/dtos';
import { CaseStatus } from '@bandido/shared/constants';
import { PaginationProps } from '@frontend/features/common/components/Table';
import { QueryKeys, SortingOptions } from '@frontend/features/common/types';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { useGetAllWorkflowsQuery, useGetWorkflowSteps } from '@frontend/features/workflows/queries';
import { useGetAllUsers } from '@frontend/features/users/queries';
import { useBulkUpdateCases } from '../queries';

interface Options {
  items: CaseResponseDto[];
  pagination?: PaginationProps;
  sorting?: { options: SortingOptions; handler: (options: SortingOptions) => void };
  filtersOptions?: { searchParams?: URLSearchParams };
  onSearchChange: (value: string) => void;
  isUnavailable?: boolean;
}

export const useCaseBulkActions = ({
  items,
  pagination,
  sorting,
  filtersOptions,
  onSearchChange,
  isUnavailable = false,
}: Options) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { addToast } = useSnackBar();
  const signature = JSON.stringify([
    pagination?.page,
    pagination?.rowsPerPage,
    sorting?.options,
    filtersOptions?.searchParams?.toString(),
  ]);
  const [selection, setSelection] = useState<{ signature: string; ids: number[] }>({ signature: '', ids: [] });
  const [bulkWorkflowId, changeWorkflow] = useState('');
  const [bulkStepId, setBulkStepId] = useState('');
  const [bulkStatus, setBulkStatus] = useState<CaseStatus | ''>('');
  const [bulkAgentValue, setBulkAgentValue] = useState('');
  const [error, setError] = useState(false);
  const inFlight = useRef(false);
  const selectedIds =
    selection.signature === signature ? selection.ids.filter((id) => items.some((item) => item.id === id)) : [];
  const clearSelection = useCallback(() => {
    setSelection({ signature: '', ids: [] });
    changeWorkflow('');
    setBulkStepId('');
    setBulkStatus('');
    setBulkAgentValue('');
    setError(false);
  }, []);
  useEffect(() => {
    clearSelection();
  }, [signature, clearSelection]);
  const setSelectedIds = (ids: number[]) => {
    if (inFlight.current || isUnavailable) return;
    setSelection({ signature, ids: [...new Set(ids)].filter((id) => items.some((item) => item.id === id)) });
    if (!ids.length) clearSelection();
  };
  const setBulkWorkflowId = (value: string) => {
    changeWorkflow(value);
    setBulkStepId('');
  };
  const handleSearchChange = (value: string) => {
    clearSelection();
    onSearchChange(value);
  };
  const paginationWithClear = pagination && {
    ...pagination,
    handlePagination: (value: number) => {
      clearSelection();
      pagination.handlePagination(value);
    },
    handleRowsPerPage: (value: number) => {
      clearSelection();
      pagination.handleRowsPerPage(value);
    },
  };
  const sortingWithClear = sorting && {
    ...sorting,
    handler: (value: SortingOptions) => {
      clearSelection();
      sorting.handler(value);
    },
  };
  const { data: workflows } = useGetAllWorkflowsQuery({ currentPage: 1, pageSize: 1000, search: '' });
  const { data: workflowSteps, isFetching: isLoadingSteps } = useGetWorkflowSteps(bulkWorkflowId || undefined);
  const { data: agents } = useGetAllUsers({ currentPage: 1, pageSize: 1000, search: '' });
  const workflowOptions = workflows?.items || [];
  const stepOptions = workflowSteps || [];
  const agentOptions = agents?.items || [];
  const statusOptions = [
    CaseStatus.canceled,
    CaseStatus.done,
    CaseStatus.inProgress,
    CaseStatus.needsAgentAction,
    CaseStatus.needsClientAction,
    CaseStatus.open,
  ].map((id) => ({ id, name: t(`cases.status.${id}`) }));
  const workflowLabelMap = new Map(workflowOptions.map((workflow) => [workflow.id, workflow.name]));
  const stepLabelMap = new Map(stepOptions.map((step) => [step.id, step.name]));
  const agentLabelMap = new Map(
    agentOptions.map((agent) => [agent.clientId, `${agent.firstName} ${agent.lastName}`.trim()]),
  );
  const statusLabelMap = new Map(statusOptions.map((status) => [status.id, status.name]));
  const { mutateAsync: bulkUpdateCases, isLoading: isBulkUpdating } = useBulkUpdateCases();
  const hasChanges = Boolean(bulkWorkflowId || bulkStepId || bulkStatus || bulkAgentValue);
  const handleApplyBulkUpdate = async () => {
    if (inFlight.current || isUnavailable || !selectedIds.length) return false;
    if (bulkStepId && (!bulkWorkflowId || isLoadingSteps || !stepOptions.some((step) => step.id === bulkStepId))) {
      addToast({ message: t('cases.list.bulk.workflowRequired'), color: 'error' });
      return false;
    }
    const patch: CaseBulkUpdateDto['patch'] = {};
    if (bulkWorkflowId) patch.workflowId = bulkWorkflowId;
    if (bulkStepId) patch.stepId = bulkStepId;
    if (bulkStatus) patch.status = bulkStatus;
    if (bulkAgentValue === 'unassigned') patch.agentId = null;
    else if (bulkAgentValue) patch.agentId = bulkAgentValue;
    if (!Object.keys(patch).length) {
      addToast({ message: t('cases.list.bulk.noChanges'), color: 'error' });
      return false;
    }
    inFlight.current = true;
    setError(false);
    const caseIds = [...selectedIds];
    try {
      const result = await bulkUpdateCases({ caseIds, patch });
      addToast({ message: t('cases.list.bulk.updatedSuccess', { count: result.data.updatedCount }), color: 'success' });
      clearSelection();
      return true;
    } catch (_error) {
      setError(true);
      addToast({ message: t('cases.list.bulk.partialError'), color: 'error' });
      return false;
    } finally {
      // The endpoint updates cases sequentially, so even a failed batch may have changed some cases.
      void queryClient.invalidateQueries(QueryKeys.getAllCases);
      caseIds.forEach((id) => {
        void queryClient.invalidateQueries([QueryKeys.getCaseById, id]);
      });
      inFlight.current = false;
    }
  };
  return {
    selectedIds,
    setSelectedIds,
    clearSelection,
    handleSearchChange,
    paginationWithClear,
    sortingWithClear,
    bulkWorkflowId,
    setBulkWorkflowId,
    bulkStepId,
    setBulkStepId,
    bulkStatus,
    setBulkStatus,
    bulkAgentValue,
    setBulkAgentValue,
    workflowOptions,
    stepOptions,
    agentOptions,
    statusOptions,
    workflowLabelMap,
    stepLabelMap,
    agentLabelMap,
    statusLabelMap,
    isBulkUpdating,
    isLoadingSteps,
    isUnavailable,
    hasChanges,
    error,
    handleApplyBulkUpdate,
  };
};
